import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Combobox } from "@/components/ui/combobox";
import { Badge } from "@/components/ui/badge";
import { MessageCircle, Smartphone, Mail, ArrowLeft, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { normalizeNlPhone } from "@/lib/format";

const isMobile = (phone) => normalizeNlPhone(phone || "").startsWith("+316");
const hasEmail = (email) => !!(email && email.includes("@"));

export default function QuickMessageDialog({ open, onOpenChange, contacts = [], templates = [], defaultTemplate = null, logPayload = {}, onLogged }) {
  const list = (contacts || []).filter((c) => c && (isMobile(c.phone) || hasEmail(c.email)));
  const [step, setStep] = useState(0);
  const [sel, setSel] = useState(0);
  const [channels, setChannels] = useState({ whatsapp: false, sms: false, email: false });
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setStep(0); setSel(0); setChannels({ whatsapp: false, sms: false, email: false }); setSubject(defaultTemplate?.subject || ""); setMessage(defaultTemplate?.body || ""); } }, [open]);

  const c = list[sel] || {};
  const canWa = isMobile(c.phone);
  const canEmail = hasEmail(c.email);
  const toggle = (k) => setChannels((cur) => ({ ...cur, [k]: !cur[k] }));

  const send = async () => {
    const norm = normalizeNlPhone(c.phone || "");
    const chosen = Object.entries(channels).filter(([, v]) => v).map(([k]) => k);
    if (chosen.length === 0) { toast.error("Kies minstens één kanaal"); return; }
    if (channels.email && !message.trim()) { toast.error("Typ een berichttekst voor de e-mail"); return; }
    setBusy(true);
    try {
      if (channels.whatsapp && canWa) {
        window.open(`https://wa.me/${norm.replace(/[^\d]/g, "")}${message ? `?text=${encodeURIComponent(message)}` : ""}`, "_blank");
        await api.post("/calllogs", { ...logPayload, date: new Date().toISOString().slice(0, 10), type: "whatsapp", outcome: `WhatsApp naar ${c.name || norm}${message ? `: ${message}` : ""}`, notes: `WhatsApp naar ${c.name || norm}${message ? `: ${message}` : ""}`, delivery_channel: "whatsapp", delivery_status: "verstuurd_apparaat", delivery_at: new Date().toISOString() });
      }
      if (channels.sms && canWa) {
        window.open(`sms:${norm}${message ? `?&body=${encodeURIComponent(message)}` : ""}`, "_self");
        await api.post("/calllogs", { ...logPayload, date: new Date().toISOString().slice(0, 10), type: "sms", outcome: `SMS naar ${c.name || norm}${message ? `: ${message}` : ""}`, notes: `SMS naar ${c.name || norm}${message ? `: ${message}` : ""}`, delivery_channel: "sms", delivery_status: "verstuurd_apparaat", delivery_at: new Date().toISOString() });
      }
      if (channels.email && canEmail) {
        await api.post("/messages/email", { ...logPayload, to: [c.email], subject: subject || "Bericht van Praktisch-Debiteurenbeheer", body: message });
      }
      toast.success("Bericht(en) verstuurd en vastgelegd in het logboek");
      onLogged && onLogged();
      onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Versturen mislukt"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="quick-message-dialog">
        <DialogHeader>
          <DialogTitle>Bericht versturen {step === 1 && "— kanaal & tekst"}</DialogTitle>
          <DialogDescription>{step === 0 ? "Selecteer naar welk contact je een bericht wilt sturen." : "Kies de kanalen en stel je bericht op. Alles wordt vastgelegd in communicatie & tijdslijn."}</DialogDescription>
        </DialogHeader>

        {step === 0 && (
          <div className="space-y-2 max-h-[55vh] overflow-y-auto">
            {list.length === 0 && <p className="text-sm text-slate-400">Geen contactgegevens (mobiel +316 of e-mail) beschikbaar.</p>}
            <RadioGroup value={String(sel)} onValueChange={(v) => setSel(Number(v))} className="space-y-2">
              {list.map((ct, i) => (
                <label key={i} className={`flex items-start gap-2 border rounded-lg px-3 py-2 cursor-pointer ${String(sel) === String(i) ? "ring-2 ring-blue-600/40 border-blue-600 bg-blue-50/30" : "border-slate-200"}`} data-testid={`msg-contact-${i}`}>
                  <RadioGroupItem value={String(i)} className="mt-1" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800">{ct.name || "Contact"} {ct.role && <span className="text-xs font-normal text-slate-400">· {ct.role}</span>}</div>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {isMobile(ct.phone) && <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[11px]">{normalizeNlPhone(ct.phone)}</Badge>}
                      {ct.phone && !isMobile(ct.phone) && <Badge variant="outline" className="text-[11px] text-slate-400">{normalizeNlPhone(ct.phone)} (geen mobiel)</Badge>}
                      {hasEmail(ct.email) && <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 text-[11px]">{ct.email}</Badge>}
                    </div>
                  </div>
                </label>
              ))}
            </RadioGroup>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            <div className="text-sm text-slate-600">Naar: <b>{c.name || normalizeNlPhone(c.phone) || c.email}</b></div>
            {templates.length > 0 && (
              <div>
                <Label className="text-xs text-slate-500">Standaardtekst (herinneringsfase)</Label>
                {defaultTemplate && <p className="text-xs text-emerald-600 mb-1">Voorgesteld op basis van fase: {defaultTemplate.label}</p>}
                <Combobox testId="msg-template-select" value="" onChange={(v) => { const tpl = templates[Number(v)]; if (tpl) { setMessage(tpl.body || ""); if (tpl.subject) setSubject(tpl.subject); } }} placeholder="Kies een standaardtekst…" searchPlaceholder="Zoek tekst…" options={templates.map((tpl, i) => ({ value: String(i), label: tpl.label }))} />
              </div>
            )}
            <div>
              <Label className="text-xs text-slate-500 mb-2 block">Kanaal</Label>
              <div className="flex flex-wrap gap-3">
                <label className={`flex items-center gap-2 text-sm border rounded-lg px-3 py-2 ${canWa ? "cursor-pointer" : "opacity-40 cursor-not-allowed"}`}>
                  <Checkbox checked={channels.whatsapp} disabled={!canWa} onCheckedChange={() => canWa && toggle("whatsapp")} data-testid="msg-channel-whatsapp" /><MessageCircle className="h-4 w-4 text-emerald-600" /> WhatsApp
                </label>
                <label className={`flex items-center gap-2 text-sm border rounded-lg px-3 py-2 ${canWa ? "cursor-pointer" : "opacity-40 cursor-not-allowed"}`}>
                  <Checkbox checked={channels.sms} disabled={!canWa} onCheckedChange={() => canWa && toggle("sms")} data-testid="msg-channel-sms" /><Smartphone className="h-4 w-4 text-blue-600" /> SMS
                </label>
                <label className={`flex items-center gap-2 text-sm border rounded-lg px-3 py-2 ${canEmail ? "cursor-pointer" : "opacity-40 cursor-not-allowed"}`}>
                  <Checkbox checked={channels.email} disabled={!canEmail} onCheckedChange={() => canEmail && toggle("email")} data-testid="msg-channel-email" /><Mail className="h-4 w-4 text-violet-600" /> E-mail
                </label>
              </div>
              {!canWa && <p className="text-xs text-slate-400 mt-1">WhatsApp/SMS alleen mogelijk bij een mobiel nummer (+316…).</p>}
            </div>
            {channels.email && (
              <div><Label className="text-xs text-slate-500">Onderwerp (e-mail)</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Onderwerp" data-testid="msg-email-subject" /></div>
            )}
            <div><Label className="text-xs text-slate-500">Bericht</Label><Textarea rows={5} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Typ hier je bericht…" data-testid="msg-body" /></div>
          </div>
        )}

        <DialogFooter>
          {step === 1 && <Button variant="outline" onClick={() => setStep(0)} data-testid="msg-back"><ArrowLeft className="h-4 w-4 mr-1" /> Terug</Button>}
          {step === 0
            ? <Button className="bg-blue-900 hover:bg-blue-800" disabled={list.length === 0} onClick={() => setStep(1)} data-testid="msg-next">Volgende <ArrowRight className="h-4 w-4 ml-1" /></Button>
            : <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={send} data-testid="msg-send">{busy ? "Versturen…" : "Versturen"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
